/* Product Quiz — answers, saved progress and scoring (window.ProductQuizState) */
(function () {
  'use strict';

  const STORAGE_KEY = 'wfh:product-quiz';

  // How long saved progress lasts
  const STORAGE_TTL = 24 * 60 * 60 * 1000;

  // Q1 answers — decide which questions and pools show
  const PATHS = ['harness', 'dildo', 'both'];

  const state = {
    // 'harness' | 'dildo' | 'both', empty until Q1 is answered
    path: '',
    // questionId -> optionId (single) or [optionId] (multi)
    answers: {},
    // Index into the questions this path shows, -1 while on the intro
    step: -1,
    // True once the shopper reaches the results screen
    finished: false,
  };

  // handle -> { pool, title, scores }, fetched from quiz-scores.json
  let scores = null;

  // { harness: [...], dildo: [...] } in collection order — the tiebreak order
  let products = { harness: [], dildo: [] };

  let questions = [];

  const subscribers = new Map();

  let didInit = false;

  /* Pub/sub */

  function subscribe(event, fn) {
    if (!subscribers.has(event)) subscribers.set(event, new Set());
    subscribers.get(event).add(fn);
  }

  function unsubscribe(event, fn) {
    const set = subscribers.get(event);
    if (set) set.delete(fn);
  }

  // Log a failing subscriber and carry on with the rest
  function emit(event, detail) {
    const set = subscribers.get(event);
    if (!set) return;
    set.forEach((fn) => {
      try {
        fn(detail);
      } catch (error) {
        console.error('[quiz] subscriber error: ' + event, error);
      }
    });
  }

  /* localStorage persistence */

  // Fingerprint of the question and option ids, so edits clear old saves
  function definitionStamp() {
    const shape = questions
      .map((q) => q.id + ':' + q.options.map((o) => o.id).join(','))
      .join('|');

    // djb2 hash
    let hash = 5381;
    for (let i = 0; i < shape.length; i++) {
      hash = ((hash << 5) + hash + shape.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
  }

  // Save progress, ignoring storage errors
  function persist() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          defs: definitionStamp(),
          path: state.path,
          answers: state.answers,
          step: state.step,
          finished: state.finished,
          savedAt: Date.now(),
        })
      );
    } catch (error) {
      console.warn('[quiz] could not persist state', error);
    }
  }

  function hydrateFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      // Saves without a timestamp count as expired
      const expired = Date.now() - (saved.savedAt || 0) > STORAGE_TTL;
      if (saved.defs !== definitionStamp() || expired) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      if (PATHS.indexOf(saved.path) !== -1) state.path = saved.path;
      state.answers = saved.answers || {};
      state.step = typeof saved.step === 'number' ? saved.step : -1;
      state.finished = saved.finished === true;
    } catch (error) {
      console.warn('[quiz] could not hydrate state', error);
    }
  }

  /* Questions */

  // The questions this path shows, in order
  function visibleQuestions() {
    const path = state.path || 'both';
    return questions.filter((q) => q.showFor.indexOf(path) !== -1);
  }

  function questionAt(index) {
    return visibleQuestions()[index] || null;
  }

  function totalSteps() {
    return visibleQuestions().length;
  }

  /* Answers */

  function getAnswer(questionId) {
    return state.answers[questionId];
  }

  // Single-select stores an optionId, multi-select stores an array
  function setAnswer(questionId, value) {
    state.answers[questionId] = value;

    // Changing Q1 drops answers to questions the new path hides
    const question = questions.find((q) => q.id === questionId);
    if (question && question.routing) {
      state.path = value;
      pruneAnswers();
    }

    persist();
    emit('quiz:answer-changed', { questionId, value });
    emit('quiz:state-changed', {});
  }

  // Toggle one option in a multi-select answer
  function toggleAnswer(questionId, optionId) {
    const current = state.answers[questionId] || [];
    const next =
      current.indexOf(optionId) === -1
        ? current.concat(optionId)
        : current.filter((id) => id !== optionId);

    setAnswer(questionId, next);
  }

  // Drop answers to questions the current path no longer shows
  function pruneAnswers() {
    const visible = visibleQuestions().map((q) => q.id);
    Object.keys(state.answers).forEach((id) => {
      if (visible.indexOf(id) === -1) delete state.answers[id];
    });
  }

  /* Navigation */

  function setStep(index) {
    state.step = index;
    state.finished = index >= totalSteps();

    persist();
    emit('quiz:state-changed', { step: index });
  }

  function reset() {
    state.path = '';
    state.answers = {};
    state.step = -1;
    state.finished = false;

    persist();
    emit('quiz:reset', {});
    emit('quiz:state-changed', {});
  }

  /* Scoring */

  // Match = the sum of the score columns behind every picked option, skipping Q1
  function selectedKeys() {
    const keys = [];

    visibleQuestions().forEach((question) => {
      if (question.routing) return;

      const answer = state.answers[question.id];
      if (!answer) return;

      const chosen = Array.isArray(answer) ? answer : [answer];
      chosen.forEach((id) => {
        const option = question.options.find((o) => o.id === id);
        if (option && option.key) keys.push(option.key);
      });
    });

    return keys;
  }

  // Missing keys score 0
  function scoreFor(handle, keys) {
    const entry = scores && scores[handle];
    if (!entry) return 0;
    return keys.reduce((sum, key) => sum + (entry.scores[key] || 0), 0);
  }

  // Rank a pool's candidates, best first, with ties in collection order
  function rank(pool, candidates) {
    const list = candidates || getProducts(pool);
    const keys = selectedKeys();

    return list
      .map((product, index) => ({
        product: product,
        index: index,
        score: scoreFor(product.handle, keys),
      }))
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .map((row) =>
        Object.assign({}, row.product, { score: row.score })
      );
  }

  // Ranked results for each pool Q1 shows
  function getResults() {
    const pools = state.path === 'both' ? ['harness', 'dildo'] : [state.path];
    const results = {};

    pools.forEach((pool) => {
      if (pool) results[pool] = rank(pool);
    });

    return results;
  }

  // Warn when a collection product has no row in the scores sheet
  function auditHandles() {
    if (!scores) return;

    ['harness', 'dildo'].forEach((pool) => {
      const missing = getProducts(pool)
        .map((product) => product.handle)
        .filter((handle) => !scores[handle]);

      if (missing.length) {
        console.warn(
          `[quiz] ${pool}: ${missing.length} product(s) missing from quiz-scores.json`,
          missing
        );
      }
    });
  }

  /* Data */

  function getScores() {
    return scores;
  }

  function getProducts(pool) {
    return pool ? products[pool] || [] : products;
  }

  // Shared scores fetch
  let scoresRequest = null;

  // Fetch scores in the background
  function loadScores(url) {
    if (scoresRequest) return scoresRequest;

    scoresRequest = fetch(url)
      .then((response) => response.json())
      .then((data) => {
        scores = data;
        auditHandles();
        emit('quiz:scores-loaded', { scores });
        return scores;
      })
      .catch((error) => {
        console.error('[quiz] could not load scores', error);
        return null;
      });

    return scoresRequest;
  }

  // Copy of the state
  function getState() {
    return JSON.parse(JSON.stringify(state));
  }

  // Read the inline JSON, restore saved answers and fire quiz:hydrated
  function init() {
    if (didInit) return;
    didInit = true;

    const root = document.querySelector('.js-quiz-root');
    if (!root) return;

    questions = decodeQuestions(readJSON(root, '.js-quiz-questions').questions || []);
    products = readJSON(root, '.js-quiz-products');

    hydrateFromStorage();
    loadScores(root.dataset.scoresUrl);

    const snapshot = getState();
    emit('quiz:hydrated', { state: snapshot });
    document.dispatchEvent(
      new CustomEvent('quiz:hydrated', { detail: { state: snapshot } })
    );
  }

  // Decode HTML entities from Liquid's `t` filter, e.g. `&#39;`
  function decodeEntities(value) {
    if (value.indexOf('&') === -1) return value;
    // Textarea content is never run as HTML
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function decodeQuestions(list) {
    list.forEach((question) => {
      question.title = decodeEntities(question.title);
      question.options.forEach((option) => {
        option.label = decodeEntities(option.label);
      });
    });
    return list;
  }

  function readJSON(root, selector) {
    const node = root.querySelector(selector);
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[quiz] could not parse ' + selector, error);
      return {};
    }
  }

  window.ProductQuizState = {
    init,
    getState,
    getAnswer,
    setAnswer,
    toggleAnswer,
    setStep,
    reset,
    visibleQuestions,
    questionAt,
    totalSteps,
    getScores,
    getProducts,
    selectedKeys,
    rank,
    getResults,
    loadScores,
    subscribe,
    unsubscribe,
  };

  init();
})();
